import { Button, Checkbox, DatePicker, Image, Space, TableProps } from "antd";
import dayjs from "dayjs";
import {
  ButtonHTMLAttributes,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "../../../app/store";
import ApproverIcon from "../../../assets/icons/ApproverIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import NormalEditIcon from "../../../assets/icons/NormalEditIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import UpdateCheckIcon from "../../../assets/icons/UpdateCheckIcon";
import UploadIcon from "../../../assets/icons/UploadIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import AVATAR from "../../../assets/images/avatar.svg";
import AdditionDeductionSalaryModal from "../../../components/Employees/AdditionDeductionSalaryModal";
import AdditionDeductionSalaryView from "../../../components/Payroll/AdditionDeductionSalaryView";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../constants/constants";
import {
  useBulkAdditionDeductionSalaryApproveMutation,
  useGetAdditionDeductionIdQuery,
  useGetAdditionDeductionSalaryQuery,
  useSingleAdditionDeductionSalaryApproveMutation,
} from "../../../features/payrollSetting/payrollSettingApi";
import useCheckTable from "../../../hooks/useCheckTable";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  GetVariableSalaryParamsType,
  GetVariableSalaryResponseType,
} from "../../../types/payroll";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray } from "../../../utils/utils";
import {
  payrollBetaSettingsTabList,
  payrollManagerSettingTabList,
} from "./PayrollSummary";

type VariableSalaryType =
  GetVariableSalaryResponseType["data"]["variableSalaries"][0];

type ModalType =
  | "REPORT_REQUEST"
  | "ADDITION_DEDUCTION_SALARY"
  | "APPROVE"
  | "BULK_APPROVE"
  | "UPDATE_PRICE";

type UpdatePriceStateType = {
  id: null | number;
  price: number;
};

export default function AdditionDeductionsSalary() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { open, handleClose, handleOpen } = useModal<any, ModalType>();
  const [updatePrice, setUpdatePrice] = useState<UpdatePriceStateType>({
    id: null,
    price: 0,
  });
  const { page, limit, filter, handleFilter, handleLimit, handlePagination } =
    useTable<GetVariableSalaryParamsType>({
      defaultFilter: {
        month_year: dayjs().format("MM-YYYY"),
      },
    });
  const { authInfo } = useAppSelector((state) => state.auth);
  const params = {
    ...filter,
    manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
  };
  const { isLoading, isError, error, data, isFetching } =
    useGetAdditionDeductionSalaryQuery({
      params,
    });
  const {
    isLoading: isIdLoading,
    isError: isIdError,
    error: idError,
    data: idData,
    isFetching: isIdFetching,
  } = useGetAdditionDeductionIdQuery({
    params: {
      ...params,
      skip: 0,
      limit: 0,
    },
  });
  const { selectedIds, checkedIds, handleCheck, handleCheckAll } =
    useCheckTable(idData?.data?.additionDeductionSalariesIds || []);
  const [
    singleAdditionDeductionSalaryApprove,
    {
      isLoading: isSingleApproveLoading,
      isError: isSingleApproveError,
      isSuccess: isSingleApproveSuccess,
      error: singleApproveError,
    },
  ] = useSingleAdditionDeductionSalaryApproveMutation();
  const [
    bulkAdditionDeductionSalaryApprove,
    {
      isLoading: isBulkApproveLoading,
      isError: isBulkApproveError,
      isSuccess: isBulkApproveSuccess,
      error: bulkApproveError,
    },
  ] = useBulkAdditionDeductionSalaryApproveMutation();
  useError(isError, error, "Get addition deduction salary data fail");
  useError(isIdError, idError, "Get addition deduction salary data fail");
  useError(
    isSingleApproveError,
    singleApproveError,
    "Fail to approve addition deduction salary"
  );
  useError(
    isBulkApproveError,
    bulkApproveError,
    "Fail to approve addition deduction salary"
  );

  // const taxReportData = data?.data?.formattedEmployees?.map((item) => ({
  //   ...item,
  //   ...calculateTax(Number(item?.gross_salary), item?.gender),
  // }));

  const columns: TableProps<VariableSalaryType>["columns"] = [
    {
      title: () => {
        const enabledRows = checkedIds.filter((el) => !el.disabled);
        return (
          <Checkbox
            indeterminate={
              selectedIds.length > 0 && selectedIds.length < enabledRows.length
            }
            checked={
              selectedIds.length > 0 &&
              selectedIds.length === enabledRows.length
            }
            onChange={(e) => handleCheckAll(e.target.checked)}
          />
        );
      },
      align: "center",
      width: 60,
      dataIndex: "id",
      render: (val, record) => {
        const isDisabled = record?.is_approved;
        return (
          <Checkbox
            disabled={isDisabled}
            checked={selectedIds.includes(val)}
            onChange={(e) => handleCheck(e.target.checked, val, isDisabled)}
          />
        );
      },
    },
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "ID",
      dataIndex: "businessMember",

      sorter: (a, b) =>
        Number(a?.businessMember?.employee_id ?? 0) -
        Number(b?.businessMember?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-nowrap ">
          {val?.employee_id ?? "n/a"}
        </span>
      ),
    },
    {
      title: "Employee Name",
      dataIndex: "businessMember",
      sorter: (a, b) =>
        a?.businessMember?.member?.profile?.name?.localeCompare(
          b?.businessMember?.member?.profile?.name
        ),
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <div className="inline-flex items-center gap-2">
          <Image
            width={30}
            height={30}
            className="object-cover rounded-full"
            src={val?.member?.profile?.pro_pic || AVATAR}
          />
          <h6>{val?.member?.profile?.name}</h6>
        </div>
      ),
    },
    {
      title: "Effective Month",
      dataIndex: "effective_month",

      render: (val) =>
        val ? dayjs(val, dbDateFormat).format("MMM, YYYY") : "n/a",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (val, record) =>
        updatePrice?.id === record?.id ? (
          <PriceInput
            ref={inputRef}
            value={updatePrice?.price}
            onChange={(e) =>
              setUpdatePrice({
                id: record?.id,
                price: Number(e.target.value),
              })
            }
            onKeyDown={(e) => handleKeyDown(e, Number(record?.id))}
            buttonProps={{
              onClick: () =>
                handleOpen({
                  type: "UPDATE_PRICE",
                  data: record?.id,
                }),
            }}
          />
        ) : (
          <UpdatePrice
            onClick={() =>
              setUpdatePrice({
                id: record?.id,
                price: Number(val),
              })
            }
          >
            {val}
          </UpdatePrice>
        ),
    },
    {
      title: "Component Name",
      dataIndex: "component_name",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Component Type",
      dataIndex: "component_type",
      render: (val) => (val ? capitalize(val) : "n/a"),
    },
    {
      title: "Is Approved",
      dataIndex: "is_approved",
      align: "center",

      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Approved By",
      dataIndex: "approved_by_name",
      render: (val) => (val ? val?.split("-")[1] : "-"),
    },
    {
      title: "Action",
      dataIndex: "is_approved",
      render: (val, record) => (
        <Space>
          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: {
                  month_year: filter?.month_year,
                  business_member_id: record?.business_member_id,
                },
              })
            }
          />
          <Button
            className="action-button"
            size="small"
            icon={<ApproverIcon width={15} height={15} />}
            onClick={() => handleOpen({ type: "APPROVE", data: record?.id })}
            disabled={val}
          />
        </Space>
      ),
    },
  ];

  const handleUpdatePrice = (id: number) => {
    singleAdditionDeductionSalaryApprove({
      data: {
        amount: updatePrice?.price,
      },
      id,
    });
  };

  // Handle Enter key to update price
  const handleKeyDown = (e: React.KeyboardEvent, id: number) => {
    if (e.key === "Enter") {
      handleOpen({
        type: "UPDATE_PRICE",
        data: id,
      });
    }
  };

  const handleSingleApprove = (id: number) => {
    singleAdditionDeductionSalaryApprove({
      data: {
        is_approved: true,
      },
      id,
    });
  };

  const handleBulkApprove = () => {
    bulkAdditionDeductionSalaryApprove({
      month_year: filter.month_year,
      ids: selectedIds,
    });
  };

  useEffect(() => {
    if ((isSingleApproveSuccess && !updatePrice?.id) || isBulkApproveSuccess) {
      toast("success", "Addition deduction salary approved successfully");
      handleClose();
      handleCheckAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleApproveSuccess, isBulkApproveSuccess]);

  useEffect(() => {
    if (isSingleApproveSuccess && updatePrice?.id) {
      toast("success", "Amount update successfully");
      setUpdatePrice({
        id: null,
        price: 0,
      });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSingleApproveSuccess]);

  const tabSetting =
    authInfo?.role === "Admin"
      ? payrollBetaSettingsTabList
      : payrollManagerSettingTabList;

  return (
    <SidebarLayout title="Payroll" tabList={tabSetting}>
      {open.type === "ADDITION_DEDUCTION_SALARY" && (
        <AdditionDeductionSalaryModal
          open={open.open}
          handleClose={handleClose}
        />
      )}
      {open.type === "VIEW" && (
        <AdditionDeductionSalaryView
          open={open.open}
          data={open?.data}
          handleClose={handleClose}
        />
      )}
      {open.type === "APPROVE" && (
        <ConfirmModal
          confirmTitle="Approve"
          questionText="Are sure to approve?"
          okText="Confirm"
          open={open.open}
          handleClose={handleClose}
          handleOk={() => handleSingleApprove(Number(open?.data))}
          okLoading={isSingleApproveLoading}
        />
      )}
      {open.type === "BULK_APPROVE" && (
        <ConfirmModal
          confirmTitle="Approve"
          questionText="Are sure to approve?"
          okText="Confirm"
          open={open.open}
          handleClose={handleClose}
          handleOk={handleBulkApprove}
          okLoading={isBulkApproveLoading}
        />
      )}

      {open.type === "UPDATE_PRICE" && (
        <ConfirmModal
          confirmTitle="Update Amount"
          questionText="Are sure to update this amount?"
          okText="Confirm"
          open={open.open}
          handleClose={handleClose}
          handleOk={() => handleUpdatePrice(Number(open?.data))}
          okLoading={isSingleApproveLoading}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching || isIdLoading || isIdFetching}
        page={page}
        limit={limit}
        columns={columns}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        dataSource={addKeyInArray(data?.data.additionDeductionSalaries)}
        total={data?.data.totalAdditionDeductionSalaries}
        // rowSelection={{
        //   type: "checkbox",
        //   selectedRowKeys: selectedData,
        //   onChange: (key) => {
        //     setSelectedData(key as number[]);
        //   },
        //   getCheckboxProps: (record) => {
        //     return {
        //       disabled: record?.is_approved,
        //     };
        //   },
        //   columnWidth: 40,
        // }}
        filterSection={
          <>
            {selectedIds?.length ? (
              <Button
                icon={<ApproverIcon width={15} height={15} />}
                type="primary"
                className="sm-button !px-3"
                onClick={() =>
                  handleOpen({
                    type: "BULK_APPROVE",
                  })
                }
              >
                Bulk Approve
              </Button>
            ) : null}
            <DatePicker
              format="MMM YYYY"
              style={{ width: 140 }}
              suffixIcon={<DateIcon />}
              className="clear-button-hide"
              value={
                filter?.month_year
                  ? dayjs(filter?.month_year, "MM-YYYY")
                  : undefined
              }
              onChange={(date) => {
                if (date) handleFilter("month_year", date?.format("MM-YYYY"));
              }}
              picker="month"
              disabled={isLoading}
            />
            {authInfo?.role === "Admin" && (
              <Button
                className="upload-button"
                icon={<UploadIcon />}
                onClick={() =>
                  handleOpen({
                    type: "ADDITION_DEDUCTION_SALARY",
                  })
                }
              >
                Upload Addition Deduction Salary
              </Button>
            )}
          </>
        }
      />
    </SidebarLayout>
  );
}

type UpdatePriceProps = {
  children: number;
  className?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

// price update edit button component
const UpdatePrice = ({
  children,
  className = "",
  ...rest
}: UpdatePriceProps) => (
  <div className="flex items-center gap-2.5">
    <span className={className}>{children}</span>
    <button
      className="p-0 m-0 border-transparent bg-transparent cursor-pointer"
      {...rest}
    >
      <NormalEditIcon />
    </button>
  </div>
);

type PriceInputProps = {
  buttonProps: ButtonHTMLAttributes<HTMLButtonElement>;
} & InputHTMLAttributes<HTMLInputElement>;

// Price update input component
const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>(
  ({ buttonProps, ...rest }, ref) => (
    <div className="relative w-24">
      <input
        ref={ref}
        type="number"
        className="text-body text-sm font-roboto leading-normal py-1 px-1.5 border border-solid border-others-border rounded !pr-7 focus-within:!outline-others-border hide_number_arrow w-full"
        min={0}
        {...rest}
      />

      <button
        className="p-0 m-0 border-transparent bg-transparent cursor-pointer text-primary absolute right-1.5 top-2/4 -translate-y-2/4 flex justify-center items-center"
        {...buttonProps}
      >
        <UpdateCheckIcon />
      </button>
    </div>
  )
);
PriceInput.displayName = "PriceInput";
